import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { useSaveBarSection } from "@/components/portal/SaveBar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { cn, dirtyRing } from "@/lib/utils";

type ClientResponse = components["schemas"]["ClientResponse"];

/**
 * General tab — the Client's identity + branding. Metadata is read-only (provisioned by Dolfin);
 * branding (logo, primary colour) is editable by Client Admins/Owners and saved to PATCH /client.
 */
export function GeneralSettingsSection() {
  const { activeClient, activeClientId } = useAuth();
  const isAdmin = activeClient?.role === "Owner" || activeClient?.role === "Admin";

  const [client, setClient] = useState<ClientResponse | null>(null);
  const [logoUrl, setLogoUrl] = useState("");
  const [primaryColour, setPrimaryColour] = useState("");
  const [savedLogoUrl, setSavedLogoUrl] = useState("");
  const [savedPrimaryColour, setSavedPrimaryColour] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!activeClientId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      const { data, error: apiError } = await api.GET("/v1/me/client", {});
      if (cancelled) return;
      if (apiError || !data) {
        setError("Failed to load client.");
        setLoading(false);
        return;
      }
      setClient(data);
      setLogoUrl(data.logoUrl ?? "");
      setPrimaryColour(data.primaryColour ?? "");
      setSavedLogoUrl(data.logoUrl ?? "");
      setSavedPrimaryColour(data.primaryColour ?? "");
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [activeClientId]);

  const logoDirty = logoUrl !== savedLogoUrl;
  const colourDirty = primaryColour !== savedPrimaryColour;
  const anyDirty = logoDirty || colourDirty;

  async function save() {
    if (!anyDirty) return;
    const body: components["schemas"]["PatchClientRequest"] = {};
    if (logoDirty) body.logoUrl = logoUrl.trim() === "" ? null : logoUrl.trim();
    if (colourDirty) body.primaryColour = primaryColour.trim() === "" ? null : primaryColour.trim();

    setSaving(true);
    setError(null);
    const { data, error: apiError } = await api.PATCH("/v1/client", { body });
    setSaving(false);
    if (apiError || !data) {
      setError((apiError as { detail?: string } | undefined)?.detail ?? "Failed to save changes.");
      return;
    }
    setClient(data);
    setLogoUrl(data.logoUrl ?? "");
    setPrimaryColour(data.primaryColour ?? "");
    setSavedLogoUrl(data.logoUrl ?? "");
    setSavedPrimaryColour(data.primaryColour ?? "");
    toast.success("Client settings updated");
  }

  function reset() {
    setLogoUrl(savedLogoUrl);
    setPrimaryColour(savedPrimaryColour);
    setError(null);
  }

  useSaveBarSection({ dirty: anyDirty, saving, save, discard: reset });

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-12 text-sm text-muted-foreground">
          <Spinner className="size-4" /> Loading client settings…
        </CardContent>
      </Card>
    );
  }

  if (!client) {
    return (
      <Card>
        <CardContent className="py-12 text-sm text-destructive">
          {error ?? "Unable to load client."}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Client</CardTitle>
          <CardDescription>Read-only metadata — provisioned by Dolfin.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4 text-sm">
          <Field label="Name" value={client.name} />
          <Field label="Type" value={client.type ?? "—"} />
          <Field label="Your role" value={activeClient?.role ?? "—"} />
          <Field label="ID" mono value={client.id ?? "—"} />
          <Field label="Payment revenue share" value={`${client.paymentRevenueSharePercent}%`} />
          <Field label="Dolfin revenue share" value={`${client.dolfinRevenueSharePercent}%`} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Bill approval branding</CardTitle>
          <CardDescription>
            Shown to suppliers on the anonymous bill-approval page at pay.dolfinai.co/b/&lt;token&gt;.
            {!isAdmin && " You need Admin or Owner role to edit these."}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="logo-url">Logo URL</Label>
            <Input
              id="logo-url"
              type="url"
              placeholder="https://…"
              value={logoUrl}
              disabled={!isAdmin || saving}
              onChange={(e) => setLogoUrl(e.target.value)}
              className={cn(logoDirty && dirtyRing)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="primary-colour">Primary colour</Label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                aria-label="Pick primary colour"
                value={isValidHex(primaryColour) ? primaryColour : "#87c9f4"}
                disabled={!isAdmin || saving}
                onChange={(e) => setPrimaryColour(e.target.value)}
                className={cn(
                  "h-9 w-12 cursor-pointer rounded-md border border-input bg-transparent p-1 disabled:cursor-not-allowed disabled:opacity-50",
                  colourDirty && dirtyRing,
                )}
              />
              <Input
                id="primary-colour"
                type="text"
                placeholder="#87c9f4"
                value={primaryColour}
                disabled={!isAdmin || saving}
                onChange={(e) => setPrimaryColour(e.target.value)}
                className={cn("font-mono", colourDirty && dirtyRing)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {error && <p className="text-sm text-destructive">{error}</p>}

    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={mono ? "font-mono text-xs" : "text-sm"}>{value}</div>
    </div>
  );
}

function isValidHex(value: string): boolean {
  return /^#[0-9A-Fa-f]{6}$/.test(value);
}
